import React from 'react';

// Constants
import ColorConstants from 'Wpds/colors/ColorConstants';

// Components
import { Illustrations, WpIllustration } from 'Wpds/components/illustration';
import { WpText } from 'Wpds/components/text';
import { WpAlert } from 'Wpds/components/alert';
import { WpLink } from 'Wpds/components/link';
import { WpIcon } from 'Wpds/components/icon';
import WorkspaceDetailsListWrapper from '../WorkspaceSideRail/WorkspaceDetailsListWrapper';

// Hooks
import { useTeamInfo } from 'Shared/utils/hooks/use-team-info';

// Utils
import getGrammarArticle from 'Shared/utils/getGrammerArticle';

// Styles
import './WorkspaceTeamSnapshot.scss';

const WorkspaceTeamSnapshot: React.FC = () => {
  const baseClassName = 'WorkspaceTeamSnapshot';
  const { teamType, shortTeamDescription, teamReminders, teamConversationTips, loading, error } = useTeamInfo();

  if (loading) {
    return (
      <div className={`${baseClassName}__loading`}>
        <WpText>Loading...</WpText>
      </div>
    );
  }

  if (error || !teamType) {
    return (
      <div className={`${baseClassName}__error`}>
        <WpAlert>
          <WpText>Unable to load team information. Please try again later.</WpText>
        </WpAlert>
      </div>
    );
  }

  const getIllustrationForTeamType = (type: string) => {
    const teamTypeName = type.toLowerCase();
    const illustrationKey = `team_type_${teamTypeName}`;
    return Illustrations[illustrationKey];
  };

  const getTeamTypeLink = (type: string) => {
    const teamTypeName = type.toLowerCase();
    const teamTypeLink = `https://www.predictiveindex.com/team-type/${teamTypeName}`;
    return teamTypeLink;
  };

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__header`}>
        <div className={`${baseClassName}__header__title`}>
          <WpIllustration src={getIllustrationForTeamType(teamType)}></WpIllustration>
          <WpText bold noMargin>
            {/* Some of our team types start with vowels, so the helper function helps us determine
            if we should use "a" or "an" before the team type. */}
            You are {getGrammarArticle(teamType)} {teamType} team
          </WpText>
        </div>
        <WpText qaTest="teamShortDescription" variant="bodySm" color="gray-600">
          {shortTeamDescription}
        </WpText>
      </div>

      <div className={`${baseClassName}__note`}>
        <WpAlert type="info">
          <p>
            <WpText variant="bodySm" tag="span" bold>
              Note:{' '}
            </WpText>
            <WpText variant="bodySm" tag="span">
              Your Team Type represents your collective team identity, and is based on employees with completed PI
              Behavioral Assessments.
            </WpText>
          </p>
          <WpText variant="bodySm" tag="span">
            <WpLink href={getTeamTypeLink(teamType)}>
              <WpIcon name="external-link" />
              Learn more about your Team Type
            </WpLink>
          </WpText>
        </WpAlert>
      </div>

      {teamConversationTips && teamConversationTips.length > 0 && (
        <WorkspaceDetailsListWrapper
          title="Team conversation tips"
          qaTest="teamConversationTips"
          icon="lightbulb-on"
          iconColor={ColorConstants.blue[500]}
          list={teamConversationTips}
        />
      )}

      {teamReminders && teamReminders.length > 0 && (
        <WorkspaceDetailsListWrapper
          qaTest="teamReminders"
          title="Team reminders"
          icon="pause-circle"
          iconColor={ColorConstants.orange[500]}
          list={teamReminders}
        />
      )}
    </div>
  );
};

export default WorkspaceTeamSnapshot;
